import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { LoginScreenNarrativeModel } from "services/api/cplauthapi/CPLAuthAPI";
import { ICompanyBrandingService } from "services/CompanyBrandingService";
import { RootState } from "setup";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";

type ClientPortalProps = {
  loginScreenNarrative: LoginScreenNarrativeModel;
  isLoading: boolean;
};

const initialState: ClientPortalProps = {
  loginScreenNarrative: new LoginScreenNarrativeModel(),
  isLoading: false,
};

export const ClientPortalLoginSlice = createSlice({
  name: "ClientPortalLogin",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getCompanyLoginScreenNarrative.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getCompanyLoginScreenNarrative.fulfilled, (state, action) => {
      state.loginScreenNarrative = action.payload;
      state.isLoading = false;
    });
    builder.addCase(getCompanyLoginScreenNarrative.rejected, (state) => {
      state.loginScreenNarrative = new LoginScreenNarrativeModel();
      state.isLoading = false;
    });
    builder.addCase(createNewCompanyLoginScreenNarrative.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createNewCompanyLoginScreenNarrative.fulfilled, (state) => {
      state.isLoading = false;
      toast.success("Login narrative updated");
    });
    builder.addCase(createNewCompanyLoginScreenNarrative.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(updateCompanyLoginScreenNarrative.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateCompanyLoginScreenNarrative.fulfilled, (state) => {
      state.isLoading = false;
      toast.success("Login narrative updated");
    });
    builder.addCase(updateCompanyLoginScreenNarrative.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const getCompanyLoginScreenNarrative = createAsyncThunk(
  "ClientPortalLogin/getCompanyLoginScreenNarrative",
  async () => {
    const companyBrandingService = container.get<ICompanyBrandingService>(SERVICE_TYPES.CompanyBrandingService);
    return await companyBrandingService.getCompanyLoginScreenNarrative();
  }
);

export const createNewCompanyLoginScreenNarrative = createAsyncThunk<
  Promise<void>,
  { loginScreenNarrative: string },
  { state: RootState }
>(
  "ClientPortalLogin/createNewCompanyLoginScreenNarrative",
  async ({ loginScreenNarrative }: { loginScreenNarrative: string }, thunkAPI) => {
    const companyBrandingService = container.get<ICompanyBrandingService>(SERVICE_TYPES.CompanyBrandingService);
    await companyBrandingService.createNewCompanyLoginScreenNarrative(loginScreenNarrative);
    thunkAPI.dispatch(getCompanyLoginScreenNarrative());
  }
);

export const updateCompanyLoginScreenNarrative = createAsyncThunk<
  Promise<void>,
  { loginScreenNarrative: LoginScreenNarrativeModel },
  { state: RootState }
>(
  "ClientPortalLogin/updateCompanyLoginScreenNarrative",
  async ({ loginScreenNarrative }: { loginScreenNarrative: LoginScreenNarrativeModel }, thunkAPI) => {
    const companyBrandingService = container.get<ICompanyBrandingService>(SERVICE_TYPES.CompanyBrandingService);
    await companyBrandingService.updateCompanyLoginScreenNarrative(loginScreenNarrative);
    thunkAPI.dispatch(getCompanyLoginScreenNarrative());
  }
);

export const selectClientPortalLoginIsLoading = (state: RootState) => {
  return state.ClientPortalLogin.isLoading;
};
export const selectClientPortalLoginScreenNarrative = (state: RootState) => {
  return state.ClientPortalLogin.loginScreenNarrative;
};

export default ClientPortalLoginSlice.reducer;
